import { useState } from 'react';
import { CasStackRow } from 'src/compass/shared/components';
import { getDateInMoment } from 'src/compass/shared/util/casMoment';

import ClaimTrackingStatusButton from './ClaimTrackingStatusButton';
import EnterActionReasonDialog from './EnterActionReasonDialog';
import NoticeReceivedDialog from './NoticeReceivedDialog';
import ClaimCallbackDialog from './ClaimCallbackDialog';
import ClaimDoneDialog from './ClaimDoneDialog';

type Props = {
  data: any;
  onUpdated: (closeParentDialogOnSuccess?: boolean) => void;
};

export type StatusButtonConfig = {
  label: string;
  url: string;
  icon?: string;
  showAsLink?: boolean;
  color?: 'primary' | 'secondary' | 'error';
  dialog?: React.ComponentType<any>;
  dialogProps?: Function | Record<string, any>; // Can be function or object
  payload?: Record<string, boolean>;
  closeParentDialogOnSuccess?: boolean | ((data: any) => boolean);
  // Hides the button for a claim it does not apply to. Shown when omitted.
  show?: (data: any) => boolean;
  // A Yes/No question to ask before acting; no question when it returns undefined.
  confirm?: (data: any) => string | undefined;
};

export type StatusButtonRuntimeConfig = Omit<
  StatusButtonConfig,
  'closeParentDialogOnSuccess' | 'confirm'
> & {
  closeParentDialogOnSuccess?: boolean;
  confirmMessage?: string;
};

type StatusType =
  | 'null'
  | 'Send'
  | 'Docusign In Process'
  | 'Docusign Review'
  | 'Reviewed'
  | 'Attached'
  | 'Approved'
  | 'Callback'
  | 'Done';

const BACK_BUTTON: StatusButtonConfig = {
  label: 'Back',
  url: '/Sce/Claims/Back',
  showAsLink: true,
  color: 'secondary',
  dialog: EnterActionReasonDialog,
  dialogProps: (data: any) => ({ title: `Clear ${data.status}` }),
};

const CLOSE_CLAIM_BUTTON: StatusButtonConfig = {
  label: 'Close Claim',
  url: '/Sce/Claims/Close',
  icon: 'mdi:close-thick',
  showAsLink: true,
  color: 'secondary',
  dialog: EnterActionReasonDialog,
  dialogProps: { title: 'Close Claim' },
};

const ATTACHED_BUTTON: StatusButtonConfig = {
  label: 'Attached',
  url: '/Sce/Claims/Attached',
  icon: 'material-symbols:attach-file',
  // Multi-SCE claims are attached together, so attaching ahead of an unreviewed sibling is
  // usually a slip.
  confirm: (data) =>
    data?.hasOthersNotReviewed
      ? 'Not all endorsements for this claim have been reviewed. Do you still want to attach?'
      : undefined,
};

// Attaches every SCE on the Claim# at once - only when all of them are Reviewed.
const ATTACH_ALL_BUTTON: StatusButtonConfig = {
  label: 'Attach All',
  url: '/Sce/Claims/AttachAll',
  icon: 'material-symbols:attach-file-add',
  show: (data) => !!data?.canAttachAll,
};

// Only for a multi-SCE claim (Claim# on more than one SCE): its SCEs are reviewed one at a time
// but attached together, so each waits in Reviewed for the rest.
const REVIEWED_BUTTON: StatusButtonConfig = {
  label: 'Reviewed',
  url: '/Sce/Claims/Reviewed',
  icon: 'material-symbols:fact-check-outline',
  show: (data) => !!data?.hasSharedClaimNumber,
};

const STATUS_BUTTON_CONFIG: Record<StatusType, StatusButtonConfig[]> = {
  null: [
    {
      label: 'Notice Received',
      url: '/Sce/Claims/NoticeReceived',
      icon: 'tabler:message-down',
      dialog: NoticeReceivedDialog,
      closeParentDialogOnSuccess: true,
    },
  ],
  Send: [
    { 
      label: 'Notice Sent', 
      url: '/Sce/Claims/Sent', 
      icon: 'material-symbols:mail-outline', 
      closeParentDialogOnSuccess: (data) =>
        data?.whatSent === 'No Claim Notice' || data?.whatSent === 'Claim Docusign',
    },
    BACK_BUTTON,
    CLOSE_CLAIM_BUTTON,
  ],
  'Docusign In Process': [
    {
      label: 'Docusign Received',
      url: '/Sce/Claims/DocusignReceived',
      icon: 'uil:file-download-alt',
    },
    BACK_BUTTON,
    CLOSE_CLAIM_BUTTON,
  ],
  'Docusign Review': [
    REVIEWED_BUTTON,
    ATTACHED_BUTTON,
    {
      label: 'Deficient',
      url: '/Sce/Claims/CallbackCreate',
      icon: 'mdi:close-octagon',
      color: 'error',
      dialog: ClaimCallbackDialog,
      dialogProps: {
        title: 'Deficient: Assign Callback',
        defaultValues: { type: 'Deficient' },
      },
    },
    BACK_BUTTON,
    CLOSE_CLAIM_BUTTON,
  ],
  Reviewed: [ATTACH_ALL_BUTTON, ATTACHED_BUTTON, BACK_BUTTON],
  Attached: [
    {
      label: 'Approved & Verified',
      url: '/Sce/Claims/Verified',
      payload: { approved: true, verified: true },
      icon: 'material-symbols:verified',
    },
    {
      label: 'Rejected',
      url: '/Sce/Claims/CallbackCreate',
      icon: 'mdi:close-octagon',
      color: 'error',
      dialog: ClaimCallbackDialog,
      dialogProps: {
        title: 'Rejected: Assign Callback',
        defaultValues: { type: 'Rejected' },
      },
    },
    BACK_BUTTON,
  ],
  Approved: [
    {
      label: 'Verified',
      url: '/Sce/Claims/Verified',
      payload: { verified: true },
      icon: 'material-symbols:verified',
    },
    BACK_BUTTON,
  ],
  Callback: [
    {
      label: 'Callback complete (Resend ready)',
      url: '/Sce/Claims/CallbackComplete',
      icon: 'tabler:phone-done',
      dialog: ClaimCallbackDialog,
      dialogProps: (data: any) => ({
        title: 'Callback Complete',
        submitButtonLabel: 'Done',
        mutationType: 'update',
        defaultValues: {
          id: data?.callback?.id,
          type: data?.callback?.type,
          callbackNotes: data?.callback?.callbackNotes,
          assignedToEmployeeId: data?.callback?.assignedToEmployeeId,
        },
      }),
    },
    BACK_BUTTON,
    CLOSE_CLAIM_BUTTON,
  ],
  Done: [
    {
      label: 'Reopen',
      url: '/Sce/Claims/Reopen',
      icon: 'raphael:acw',
      dialog: ClaimDoneDialog,
    },
  ],
};

export default function ClaimTrackingStatusActions({ data, onUpdated }: Props) {
  const { status, id: endorsementId } = data || {};

  const [activeDialogButton, setActiveDialogButton] = useState<StatusButtonRuntimeConfig | null>(null);

  const buttons = (STATUS_BUTTON_CONFIG?.[status as StatusType] || []).filter(
    (button) => !button.show || button.show(data)
  );
  
  const handleDialogUpdated = () => {
    onUpdated?.(activeDialogButton?.closeParentDialogOnSuccess);
  };

  // Include all props that are needed by any dialog
  const defaultDialogProps = {
    onUpdated: handleDialogUpdated,
    endorsementId,
    status,
    onClose: () => setActiveDialogButton(null),
  };

  if (getDateInMoment(data.endDate).isAfter(new Date())) return null;

  const DialogComponent = activeDialogButton?.dialog;

  const dialogProps = () => {
    if (typeof activeDialogButton?.dialogProps === 'function')
      return activeDialogButton?.dialogProps(data);

    return activeDialogButton?.dialogProps;
  }

  return (
    <>
      <CasStackRow gapSize="large">
        {buttons.map((button) => {
          const closeParentDialogOnSuccess: boolean | undefined =
            typeof button.closeParentDialogOnSuccess === 'function'
              ? button.closeParentDialogOnSuccess(data)
              : button.closeParentDialogOnSuccess;

          const adjButton = { ...button, closeParentDialogOnSuccess, confirmMessage: button.confirm?.(data) };
          if (adjButton.label==="Notice Sent")  // TODO - brittle
              adjButton.label = `${data.whatSent  } Sent`;
          return <ClaimTrackingStatusButton
            key={button.label}
            button={adjButton}
            endorsementId={endorsementId}
            onUpdated={onUpdated}
            onDialogChange={setActiveDialogButton}
          />
        })}
      </CasStackRow>
      {DialogComponent && (
        <DialogComponent {...activeDialogButton} {...defaultDialogProps} {...dialogProps()} />
      )}
    </>
  );
}
